<template>
  <div>
    <div class="row">
      <div class="col-md-6 col-12">
        <q-select
          :menu-absolute="true"
          outline
          emit-value
          map-options
          multiple
          clearable
          use-input
          stack-label
          v-model="selectedStocksModel"
          :options="optionStocks"
          @update:model-value="onClickStock()"
          option-value="symbol"
          option-label="symbol"
          label="Add Stock(s)"
          @filter="filterQSelectStocks"
          class="q-ma-md"
        >
          <template v-slot:no-option>
            <q-item>
              <q-item-section class="text-grey"> No results </q-item-section>
            </q-item>
          </template>
        </q-select>
        <div v-if="stocks.length" class="q-ma-xs">
          <q-badge
            size="xs"
            class="q-ma-sm q-pa-xs"
            v-for="stockSymbol in selectedStocksModel"
            :key="stockSymbol"
          >
            {{ stockSymbol }}
            <q-btn
              dense
              round
              size="xs"
              class="q-pa-none"
              icon="fa fa-times"
              @click="removeStock(stockSymbol)"
            ></q-btn>
          </q-badge>
        </div>
      </div>
      <div class="col-md-6 col-12">
        <q-select
          outline
          emit-value
          map-options
          multiple
          clearable
          use-input
          v-model="selectedDimensionsModel"
          :options="optionDimensions"
          @update:model-value="onClickDimension()"
          option-value="name"
          option-label="verbose_name"
          label="Add Dimension(s)"
          @filter="filterQSelectDimensions"
          class="q-ma-md"
          :menu-absolute="true"
        />

        <div v-if="dimensions.length" class="q-ma-xs">
          <q-badge
            size="xs"
            class="q-ma-sm q-pa-xs text-bold"
            v-for="dimName in selectedDimensionsModel"
            :key="dimName"
          >
            {{ dimensions.filter((d) => d.name === dimName)[0]?.verbose_name }}
            <q-btn
              dense
              round
              size="xs"
              class="q-pa-none"
              icon="fa fa-times"
              @click="removeDimension(dimName)"
            ></q-btn>
          </q-badge>
        </div>
      </div>
    </div>

    <div class="q-ma-md">
      <div class="text-body1" :class="$q.dark.mode ? '' : 'text-primary'">
        Fiscal Year:
      </div>
      <q-range
        v-if="fiscalYears.length"
        class="q-px-md"
        v-model="fiscalYearKeys"
        color="blue"
        :min="0"
        :max="fiscalYears.length - 1"
        markers
        :marker-labels="
          fiscalYears.map((fy, idx) => {
            return {
              value: idx,
              label: fy,
            };
          })
        "
        label-always
        switch-label-side
        switch-marker-labels-side
        @update:model-value="onClickFiscalYear()"
        :left-label-value="fiscalYearKeys.min"
        :right-label-value="fiscalYearKeys.max"
      />
    </div>

    <q-btn
      v-if="isUserPlatinum"
      class="q-ma-sm text-right"
      :color="$q.dark.mode ? 'grey-8' : 'primary'"
      size="sm"
      @click="exportToCSV"
      >Export as CSV
    </q-btn>

    <q-tabs
      v-model="tabDisplay"
      dense
      :class="$q.dark.mode ? 'bg-grey-10' : 'text-grey-9 bg-grey-4'"
      active-color="white"
      :active-bg-color="$q.dark.mode ? 'grey-9' : 'blue-4'"
      :indicator-color="$q.dark.mode ? 'grey-7' : 'blue-5'"
      align="justify"
      narrow-indicator
    >
      <q-tab name="graph" label="Graph View" no-caps></q-tab>
      <q-tab name="tabular" label="Tabular View" no-caps></q-tab>
    </q-tabs>

    <q-tab-panels v-model="tabDisplay" animated>
      <q-tab-panel class="q-pa-md" name="graph">
        <div v-if="dimensions.length">
          <q-card
            flat
            square
            bordered
            v-for="dimensionName in selectedDimensionsModel"
            :key="dimensionName"
          >
            <div
              class="text-body1 text-bold q-ma-md"
              :class="$q.dark.mode ? '' : 'text-primary'"
              v-if="
                dimensions.filter((d) => d.name === dimensionName)?.[0]
                  ?.verbose_name
              "
            >
              {{
                dimensions.filter((d) => d.name === dimensionName)?.[0]
                  ?.verbose_name
              }}
            </div>

            <div id="chart">
              <apexcharts
                v-if="
                  stockReportsByDimensionAndStock &&
                  stockReportsByDimensionAndStock[dimensionName] &&
                  stockReportsByDimensionAndStock[dimensionName][
                    'time_period'
                  ] &&
                  stockReportsByDimensionAndStock[dimensionName]['data']
                "
                type="line"
                height="350"
                :options="{
                  chart: {
                    height: 350,
                    type: 'line',
                    zoom: { enabled: false },
                  },
                  dataLabels: { enabled: true },
                  stroke: { curve: 'smooth', width: 2 },
                  // grid: {
                  //   row: { colors: ['#f3f3f3', 'transparent'], opacity: 0.5 },
                  // },
                  xaxis: {
                    categories: stockReportsByDimensionAndStock[dimensionName][
                      'time_period'
                    ]
                      .slice(1)
                      .map((t) => t.name),
                    labels: {
                      style: {
                        colors: $q.dark.mode ? '#fff' : '#000',
                      },
                    },
                  },
                  yaxis: {
                    labels: {
                      style: {
                        colors: $q.dark.mode ? '#fff' : '#000',
                      },
                    },
                  },
                  tooltip: $q.dark.mode
                    ? { theme: 'dark' }
                    : { theme: 'light' },
                }"
                :series="stockReportsByDimensionAndStock[dimensionName]['data']"
              ></apexcharts>
            </div>
          </q-card>
        </div>
      </q-tab-panel>

      <q-tab-panel class="q-pa-none" name="tabular">
        <div v-if="dimensions.length">
          <q-card
            v-for="dimensionName in selectedDimensionsModel"
            :key="dimensionName"
            bordered
            flat
            square
          >
            <p
              class="text-body1 text-bold q-ma-md"
              :class="$q.dark.mode ? '' : 'text-primary'"
              v-if="
                dimensions.filter((d) => d.name === dimensionName)?.[0]
                  ?.verbose_name
              "
            >
              {{
                dimensions.filter((d) => d.name === dimensionName)[0]
                  .verbose_name
              }}
            </p>

            <q-table
              v-if="
                stockReportsByDimensionAndStock &&
                stockReportsByDimensionAndStock[dimensionName] &&
                stockReportsByDimensionAndStock[dimensionName]['time_period'] &&
                stockReportsByDimensionAndStock[dimensionName]['data_table']
              "
              :class="
                $q.dark.mode
                  ? 'table-sticky-header-column-dark'
                  : 'table-sticky-header-column'
              "
              :rows="
                stockReportsByDimensionAndStock[dimensionName]['data_table']
              "
              :columns="
                stockReportsByDimensionAndStock[dimensionName]['time_period']
              "
              separator="cell"
              :pagination="{
                rowsPerPage: 0,
              }"
              hide-bottom
            />
          </q-card>
        </div>
      </q-tab-panel>
    </q-tab-panels>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from "vue";
import { useQuasar } from "quasar";
import { useStore } from "vuex";
import useCore from "src/use/core";
import apexcharts from "vue3-apexcharts";
import memoize from "memoizee";

const props = defineProps({
  symbol: {
    type: String,
    required: true,
  },
  allStocks: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const $q = useQuasar();
const store = useStore();
const {
  allSector,
  sectorDetail,

  sectorReport,
  sectorDimension,
  stockReport,
} = useCore();

const isUserPlatinum = ref(false);

isUserPlatinum.value =
  store.state.packages.packageStatus?.current_package?.package_name ===
  "Platinum";

const sector = ref(null);
const sectorReportData = ref(null);

const stocks = ref([]);
const optionStocks = ref([]);
const selectedStocksModel = ref([]);

const fiscalYears = ref([]);
const fiscalYearKeys = ref({ min: 3, max: 5 });

const dimensions = ref([]);
const optionDimensions = ref([]);
const selectedDimensionsModel = ref(["roe", "roa", "base_rate", "cd_ratio"]);
const tabDisplay = ref("");
tabDisplay.value = props.allStocks ? "tabular" : "graph";

const stockReportsByStock = ref({});
const stockReportsByDimensionAndStock = ref({});

const memoizedStockReport = memoize(
  async (sectorId) => {
    const response = await stockReport(sectorId);
    return response;
  },
  { maxAge: 60 * 1000, promise: true }
);

const memoizedsectorDimension = memoize(
  async (sectorId) => {
    const response = await sectorDimension(sectorId);
    return response;
  },
  { maxAge: 60 * 1000, promise: true }
);

function loadSectorData(symbol) {
  allSector().then((res) => {
    sector.value = res.data.find((s) => s.symbol == symbol);

    memoizedsectorDimension(sector.value.id).then((res) => {
      dimensions.value = res.data;
      optionDimensions.value = res.data;
      selectedDimensionsModel.value = [
        "net_worth",
        "eps",
        "roe",
        "roa",
        "npl",
        "base_rate",
        "cd_ratio",
      ];
    });
    sectorReport(sector.value.id).then((res) => {
      sectorReportData.value = res.data.map((sr) => {
        return {
          sector_id: sr.sector_id,
          fiscal_year: sr.fiscal_year,
          quarter: sr.quarter,
          eps: sr.avg_eps,
          net_worth: sr.avg_net_worth,
          pe_ratio: sr.avg_pe_ratio,
          gram_value: sr.avg_gram_value,
          growth_rate: sr.avg_growth_rate,
          peg_value: sr.avg_peg_value,
        };
      });

      fiscalYears.value = [
        ...new Set(sectorReportData.value.map((item) => item.fiscal_year)),
      ].sort((a, b) => {
        a = a.split("/");
        b = b.split("/");
        return (
          parseInt(a[0]) + parseInt(a[1]) - parseInt(b[0]) - parseInt(b[1])
        );
      });
      fiscalYearKeys.value = {
        min: fiscalYears.value.length - 3,
        max: fiscalYears.value.length - 1,
      };
      sectorDetail(sector.value.id).then((res) => {
        stocks.value = res.data.stocks;
        if (props.allStocks) {
          selectedStocksModel.value = stocks.value.map((item) => item.symbol);
          if (selectedStocksModel.value !== null) {
            loadSelectedStockReports();
          }
        } else {
          const sectorToStocksMapping = {
            1: ["ADBL", "NBL"],
            2: ["MNBBL", "GBBL"],
            3: ["GUFL", "BFC"],
            4: ["SHL", "TRH"],
            5: ["BPCL", "CHCL"],
            6: ["CHDC", "NRN"],
            7: ["ALICL", "NLICL"],
            8: ["HDL", "SHIVAM"],
            9: ["CBBL", "DDBL"],
            10: ["NIL", "SICL"],
            11: ["NTC", "NRIC"],
            12: ["STC", "BBC"],
          };

          const selectedStocks = sectorToStocksMapping[sector.value.id];
          if (selectedStocks) {
            selectedStocksModel.value = selectedStocks.map(
              (item) =>
                res.data.stocks.find((stock) => stock.symbol === item).symbol
            );
            loadSelectedStockReports();
          }
        }
      });
    });
  });
}

function loadSelectedStockReports() {
  selectedStocksModel.value.forEach((stockSymbol) => {
    if (!stockReportsByStock.value[stockSymbol]) {
      stockReportsByStock.value[stockSymbol] = {};

      memoizedStockReport(
        stocks.value.find((s) => s.symbol == stockSymbol).id
      ).then((res) => {
        stockReportsByStock.value[stockSymbol] = res.data;

        updateReportChartTable();
      });
    }
  });
}

function updateReportChartTable() {
  optionDimensions.value.forEach((dim) => {
    if (!stockReportsByDimensionAndStock.value[dim.name]) {
      stockReportsByDimensionAndStock.value[dim.name] = {};
    }

    stockReportsByDimensionAndStock.value[dim.name]["verbose_name"] =
      dim.verbose_name;

    stockReportsByDimensionAndStock.value[dim.name]["time_period"] = [
      {
        name: "stock",
        label: "Stock",
        field: "stock",
      },
      ...(Array.isArray(stockReportsByStock.value[selectedStocksModel.value[0]])
        ? stockReportsByStock.value[selectedStocksModel.value[0]]
            .filter((sr) => {
              return fiscalYears.value
                .slice(fiscalYearKeys.value.min, fiscalYearKeys.value.max + 1)
                .includes(sr.fiscal_year);
            })
            .map((sr) => {
              return {
                name: sr["quarter"] + "-" + sr["fiscal_year"],
                label: sr["quarter"] + "-" + sr["fiscal_year"],
                field: sr["quarter"] + "-" + sr["fiscal_year"],
              };
            })
            .reverse()
        : []),
    ];

    stockReportsByDimensionAndStock.value[dim.name]["data"] = [];
    stockReportsByDimensionAndStock.value[dim.name]["data_table"] = [];

    selectedStocksModel.value.forEach((stockSymbol) => {
      stockReportsByDimensionAndStock.value[dim.name]["data"].push({
        name: stockSymbol,
        data: Array.isArray(stockReportsByStock.value[stockSymbol])
          ? stockReportsByStock.value[stockSymbol]
              .filter((sr) => {
                return fiscalYears.value
                  .slice(fiscalYearKeys.value.min, fiscalYearKeys.value.max + 1)
                  .includes(sr.fiscal_year);
              })
              .map((sr) =>
                sr[dim.name] ? parseFloat(sr[dim.name]).toFixed(2) : null
              )
              .reverse()
          : [],
      });
      stockReportsByDimensionAndStock.value[dim.name]["data_table"].push({
        stock: stockSymbol,
        ...(Array.isArray(stockReportsByStock.value[stockSymbol])
          ? stockReportsByStock.value[stockSymbol]
              .filter((sr) => {
                return fiscalYears.value
                  .slice(fiscalYearKeys.value.min, fiscalYearKeys.value.max + 1)
                  .includes(sr.fiscal_year);
              })
              .reverse()
              .reduce((acc, curr) => {
                var qtrfykey = curr["quarter"] + "-" + curr["fiscal_year"];
                if (!acc[qtrfykey]) {
                  acc[qtrfykey] = curr[dim.name]
                    ? parseFloat(curr[dim.name]).toFixed(2)
                    : "N/A";
                }
                return acc;
              }, {})
          : {}), // Default to an empty object if not an array
      });
    });

    // // Append sector report data
    // stockReportsByDimensionAndStock.value[dim.name]["data"] = [
    //   ...stockReportsByDimensionAndStock.value[dim.name]["data"],
    //   {
    //     name: "Average",
    //     data: sectorReportData.value
    //       .filter((sr) => {
    //         return fiscalYears.value
    //           .slice(fiscalYearKeys.value.min, fiscalYearKeys.value.max + 1)
    //           .includes(sr.fiscal_year);
    //       })
    //       .map((sr) =>
    //         sr[dim.name] ? parseFloat(sr[dim.name]).toFixed(2) : null
    //       )
    //       .reverse(),
    //   },
    // ];
    // stockReportsByDimensionAndStock.value[dim.name]["data_table"] = [
    //   ...stockReportsByDimensionAndStock.value[dim.name]["data_table"],
    //   {
    //     stock: "Average",
    //     ...sectorReportData.value
    //       .filter((sr) => {
    //         return fiscalYears.value
    //           .slice(fiscalYearKeys.value.min, fiscalYearKeys.value.max + 1)
    //           .includes(sr.fiscal_year);
    //       })
    //       .reverse()
    //       .reduce((acc, curr) => {
    //         var qtrfykey =
    //           curr["quarter"] +
    //           (curr["quarter"] == "q4" ? "-" + curr["fiscal_year"] : "");
    //         if (!acc[qtrfykey]) {
    //           acc[qtrfykey] = curr[dim.name]
    //             ? parseFloat(curr[dim.name]).toFixed(2)
    //             : "N/A";
    //         }
    //         return acc;
    //       }, {}),
    //   },
    // ];
  });
}

function onClickStock() {
  loadSelectedStockReports(sector.value.symbol);
  updateReportChartTable();
}
function onClickDimension() {
  updateReportChartTable();
}
function onClickFiscalYear() {
  updateReportChartTable();
}

function filterQSelectStocks(val, update) {
  if (val === "") {
    update(() => {
      optionStocks.value = stocks.value;
    });
    return;
  }

  update(() => {
    const needle = val.toLowerCase();
    optionStocks.value = stocks.value.filter((v) => {
      return v.symbol.toLowerCase().indexOf(needle) > -1;
    });
  });
}

function removeStock(clickedStockSymbol) {
  selectedStocksModel.value = selectedStocksModel.value.filter(
    (stockSymbol) => stockSymbol !== clickedStockSymbol
  );
  onClickStock(sector.value.symbol);
}

function filterQSelectDimensions(val, update) {
  if (val === "") {
    update(() => {
      optionDimensions.value = dimensions.value;
    });
    return;
  }

  update(() => {
    const needle = val.toLowerCase();
    optionDimensions.value = dimensions.value.filter((v) => {
      return v.name.toLowerCase().indexOf(needle) > -1;
    });
  });
}

function removeDimension(clickedDimName) {
  selectedDimensionsModel.value = selectedDimensionsModel.value.filter(
    (dimName) => dimName !== clickedDimName
  );
  onClickDimension();
}

onMounted(async () => {
  loadSectorData(props.symbol);

  if (!store.state.packages.packageStatus?.current_package) {
    await store.dispatch("packages/getPackageStatus");
  }
});

watch(props, (newProps, oldProps) => {
  loadSectorData(newProps.symbol);
});

function exportToCSV() {
  const data = stockReportsByDimensionAndStock.value;

  const convertToCSV = (data) => {
    let csv = "";

    // Add Chukul.com Watermark to the CSV data
    csv += `"chukul.com",\n`;

    for (const dimensionKey in data) {
      if (selectedDimensionsModel.value.includes(dimensionKey)) {
        const dimension = data[dimensionKey];
        const verboseName = dimension.verbose_name;
        const timePeriod = dimension.time_period;

        // Add dimension header
        csv += `"${verboseName}",\n`;

        csv += timePeriod.map((period) => period.label).join('","') + '"\n';

        // Add data for each stock
        for (const stock of dimension.data) {
          csv += `"${stock.name}",`;
          for (const value of stock.data) {
            csv += `"${value}",`;
          }
          csv += "\n";
        }

        csv += "\n";
      }
    }

    return csv;
  };

  // Create the CSV content
  const csvContent = convertToCSV(data);

  // Create a Blob containing the CSV data
  const blob = new Blob([csvContent], { type: "text/csv" });

  // Create a temporary URL for the Blob
  const url = window.URL.createObjectURL(blob);

  // Create a link element to trigger the download
  const a = document.createElement("a");
  a.href = url;
  a.download = "stockReport.csv";

  // Trigger a click event to initiate the download
  a.click();

  // Clean up by revoking the URL
  window.URL.revokeObjectURL(url);
}
</script>
